import { useEffect, useState } from "react";
import {
  Plus,
  Edit2,
  Trash2,
  Save,
  X,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  HandCoins,
} from "lucide-react";
import {
  fundoReservaService,
  movimentoFundoReservaService,
} from "../../services/apiService";
import { useNotification } from "../../hooks/useNotification";
import { useConfirmDialog } from "../../hooks/useConfirmDialog";
import PageContainer from "../../components/PageContainer";
import {
  Button,
  FormField,
  IconButton,
  SelectInput,
  TextAreaInput,
  TextInput,
  buttonPageActiveClass,
  buttonPageClass,
  cardClass,
  cardHeaderClass,
  cardHeaderTitleClass,
  tableBodyClass,
  tableClass,
  tableHeadClass,
  tableHeaderCellCenterClass,
  tableHeaderCellClass,
  tableHeaderCellRightClass,
  tableRowClass,
  tableWrapperClass,
} from "../../components/ui";
import {
  formatarValorReal,
  formatCurrencyMoeda,
} from "../../utils/FormatarValores";
import {
  getMesAtualInput,
  inputMesParaCompetencia,
} from "../../utils/FormatarCompetencia";


const FORM_INICIAL = {
  fundoReservaId: "",
  tipoMovimento: "ENTRADA",
  valorMovimento: 0,
  valorMovimentoFormatted: "0,00",
  observacao: "",
};

const MovimentoFundoReserva = () => {
  const [fundosReserva, setFundosReserva] = useState([]);
  const [movimentos, setMovimentos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const [filtros, setFiltros] = useState({
    fundoReservaId: "",
    competencia: getMesAtualInput(),
  });

  const [formData, setFormData] = useState(FORM_INICIAL);

  const [paginacao, setPaginacao] = useState({
    paginaAtual: 1,
    itensPorPagina: 8,
  });

  const { showSuccess, showError } = useNotification();
  const confirmDialog = useConfirmDialog();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadFundosReserva();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (fundosReserva.length > 0) {
      loadMovimentos();
    }
  }, [fundosReserva]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadFundosReserva = async () => {
    setLoading(true);
    try {
      const data = await fundoReservaService.listarFundosReserva();
      const lista = Array.isArray(data) ? data : [];
      setFundosReserva(lista);

      if (lista.length > 0) {
        const primeiroId = String(lista[0].id);
        setFormData((prev) => ({
          ...prev,
          fundoReservaId: prev.fundoReservaId || primeiroId,
        }));
      }
    } catch (err) {
      showError(err.message || "Erro ao carregar fundos de reserva");
    } finally {
      setLoading(false);
    }
  };

  const loadMovimentos = async (filtroAtual = filtros) => {
    setLoading(true);
    try {
      const competencia = inputMesParaCompetencia(filtroAtual.competencia);
      const fundoId = filtroAtual.fundoReservaId || undefined;

      const data = competencia
        ? await movimentoFundoReservaService.listarPorCompetencia(competencia, fundoId)
        : await movimentoFundoReservaService.listarPorPeriodo(fundoId);

      setMovimentos(Array.isArray(data) ? data : []);
      setPaginacao((prev) => ({ ...prev, paginaAtual: 1 }));
    } catch (err) {
      showError(err.message || "Erro ao carregar movimentações");
    } finally {
      setLoading(false);
    }
  };

  const handleFiltroChange = (e) => {
    const { name, value } = e.target;
    setFiltros((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleBuscar = async () => {
    await loadMovimentos(filtros);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleValorMovimentoChange = (e) => {
    const { formatted, numeric } = formatarValorReal(e.target.value);

    setFormData((prev) => ({
      ...prev,
      valorMovimento: numeric,
      valorMovimentoFormatted: formatted,
    }));
  };

  const handleNovo = () => {
    setEditingItem(null);
    setFormData({
      ...FORM_INICIAL,
      fundoReservaId: formData.fundoReservaId || String(fundosReserva[0]?.id || ""),
    });
    setShowForm(true);
  };

  const handleEditar = (item) => {
    const valorMovimento = Number(item.valorMovimento ?? item.valor ?? 0);

    setEditingItem(item);
    setFormData({
      fundoReservaId: String(item.fundoReservaId || ""),
      tipoMovimento: valorMovimento < 0 ? "SAIDA" : "ENTRADA",
      valorMovimento: Math.abs(valorMovimento),
      valorMovimentoFormatted: formatarValorReal(
        (Math.abs(valorMovimento) * 100).toFixed(0)
      ).formatted,
      observacao: item.observacao || item.descricao || "",
    });
    setShowForm(true);
  };

  const handleCancelar = () => {
    setShowForm(false);
    setEditingItem(null);
    setFormData(FORM_INICIAL);
  };

  const handleSubmit = async () => {
    if (!formData.fundoReservaId) {
      showError("Fundo de reserva é obrigatório");
      return;
    }

    if (
      formData.valorMovimentoFormatted === "" ||
      Number.isNaN(Number(formData.valorMovimento))
    ) {
      showError("Valor do movimento é obrigatório");
      return;
    }

    const payload = {
      fundoReservaId: Number(formData.fundoReservaId),
      valorMovimento:
        formData.tipoMovimento === "SAIDA"
          ? -Math.abs(formData.valorMovimento)
          : Math.abs(formData.valorMovimento),
      observacao: formData.observacao.trim(),
    };

    setProcessing(true);
    try {
      if (editingItem) {
        await movimentoFundoReservaService.atualizar(editingItem.id, payload);
        showSuccess("Movimentação atualizada com sucesso!");
      } else {
        await movimentoFundoReservaService.criar(payload);
        showSuccess("Movimentação criada com sucesso!");
      }

      await loadMovimentos(filtros);
      handleCancelar();
    } catch (err) {
      showError(err.message || "Erro ao salvar movimentação");
    } finally {
      setProcessing(false);
    }
  };

  const handleDelete = async (id) => {
    const confirmed = await confirmDialog({
      title: "Confirmar",
      description: "Tem certeza que deseja excluir esta movimentação?",
    });

    if (!confirmed) {
      return;
    }

    setProcessing(true);
    try {
      await movimentoFundoReservaService.deletar(id);
      showSuccess("Movimentação excluída com sucesso!");
      await loadMovimentos(filtros);
    } catch (err) {
      showError(err.message || "Erro ao excluir movimentação");
    } finally {
      setProcessing(false);
    }
  };

  const totalRegistros = movimentos.length;
  const totalPaginas = Math.ceil(totalRegistros / paginacao.itensPorPagina);
  const indiceInicial = (paginacao.paginaAtual - 1) * paginacao.itensPorPagina;
  const indiceFinal = indiceInicial + paginacao.itensPorPagina;
  const movimentosPaginados = movimentos.slice(indiceInicial, indiceFinal);

  const irParaPrimeiraPagina = () => {
    setPaginacao((prev) => ({ ...prev, paginaAtual: 1 }));
  };

  const irParaUltimaPagina = () => {
    setPaginacao((prev) => ({ ...prev, paginaAtual: totalPaginas }));
  };

  const irParaPaginaAnterior = () => {
    setPaginacao((prev) => ({
      ...prev,
      paginaAtual: Math.max(1, prev.paginaAtual - 1),
    }));
  };

  const irParaProximaPagina = () => {
    setPaginacao((prev) => ({
      ...prev,
      paginaAtual: Math.min(totalPaginas, prev.paginaAtual + 1),
    }));
  };

  const irParaPagina = (pagina) => {
    setPaginacao((prev) => ({
      ...prev,
      paginaAtual: pagina,
    }));
  };

  const gerarNumerosPaginas = () => {
    const paginas = [];
    const maxPaginasVisiveis = 5;
    let inicio = Math.max(
      1,
      paginacao.paginaAtual - Math.floor(maxPaginasVisiveis / 2)
    );
    let fim = Math.min(totalPaginas, inicio + maxPaginasVisiveis - 1);

    if (fim - inicio < maxPaginasVisiveis - 1) {
      inicio = Math.max(1, fim - maxPaginasVisiveis + 1);
    }

    for (let i = inicio; i <= fim; i++) {
      paginas.push(i);
    }

    return paginas;
  };

  const getNomeFundo = (item) => {
    return (
      item.fundoReservaNome ||
      item.fundoNome ||
      fundosReserva.find((fundo) => Number(fundo.id) === Number(item.fundoReservaId))
        ?.nomeFundo ||
      "-"
    );
  };

  return (
    <PageContainer className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2 flex items-center gap-2">
          <HandCoins size={24} />
          Movimentação de Fundo de Reserva
        </h1>
        <p className="text-gray-600">
          Lance e gerencie os registros de movimentação dos fundos de reserva
        </p>
      </div>

      <div className={`${cardClass} p-4 mb-6`}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <FormField label="Fundo de Reserva" htmlFor="filtro-fundo-reserva">
            <SelectInput
              id="filtro-fundo-reserva"
              name="fundoReservaId"
              value={filtros.fundoReservaId}
              onChange={handleFiltroChange}
            >
              <option value="">Todos</option>
              {fundosReserva.map((fundo) => (
                <option key={fundo.id} value={fundo.id}>
                  {fundo.nomeFundo}
                </option>
              ))}
            </SelectInput>
          </FormField>

          <FormField label="Competência" htmlFor="filtro-competencia">
            <TextInput
              id="filtro-competencia"
              type="month"
              name="competencia"
              value={filtros.competencia}
              onChange={handleFiltroChange}
            />
          </FormField>

          <div className="flex items-end gap-2">
            <Button
              onClick={handleBuscar}
              disabled={loading}
              icon={RefreshCw}
            >
              Buscar
            </Button>

            <Button
              onClick={handleNovo}
              disabled={processing || fundosReserva.length === 0}
              variant="success"
              icon={Plus}
            >
              Nova Movimentação
            </Button>
          </div>
        </div>
      </div>

      {showForm && (
        <div className="mb-6 rounded-xl border border-gray-200 bg-gray-50 p-6">
          <h3 className="text-lg font-semibold mb-4">
            {editingItem ? "Editar Movimentação" : "Nova Movimentação"}
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField label="Fundo de Reserva" required htmlFor="fundoReservaId">
              <SelectInput
                id="fundoReservaId"
                name="fundoReservaId"
                value={formData.fundoReservaId}
                onChange={handleFormChange}
              >
                <option value="">Selecione...</option>
                {fundosReserva.map((fundo) => (
                  <option key={fundo.id} value={fundo.id}>
                    {fundo.nomeFundo}
                  </option>
                ))}
              </SelectInput>
            </FormField>

            <FormField label="Tipo de Movimentação" required htmlFor="tipoMovimento">
              <SelectInput
                id="tipoMovimento"
                name="tipoMovimento"
                value={formData.tipoMovimento}
                onChange={handleFormChange}
              >
                <option value="ENTRADA">Entrada</option>
                <option value="SAIDA">Saída</option>
              </SelectInput>
            </FormField>

            <FormField label="Valor do Movimento" required htmlFor="valorMovimento">
              <TextInput
                type="text"
                id="valorMovimento"
                name="valorMovimento"
                value={formData.valorMovimentoFormatted}
                onChange={handleValorMovimentoChange}
                placeholder="0,00"
              />
            </FormField>

            <FormField className="md:col-span-2" label="Observação" htmlFor="observacao">
              <TextAreaInput
                id="observacao"
                name="observacao"
                value={formData.observacao}
                onChange={handleFormChange}
                maxLength={500}
              />
            </FormField>
          </div>

          <div className="flex gap-2 mt-4">
            <Button
              onClick={handleSubmit}
              disabled={processing}
              variant="success"
              icon={Save}
            >
              {processing ? "Salvando..." : "Salvar"}
            </Button>

            <Button
              onClick={handleCancelar}
              disabled={processing}
              variant="secondary"
              icon={X}
            >
              Cancelar
            </Button>
          </div>
        </div>
      )}

      <div className={`${cardClass} overflow-hidden`}>
        <div className={cardHeaderClass}>
          <h3 className={cardHeaderTitleClass}>Lista de Movimentações</h3>
          <span className="text-sm text-gray-500">
            {totalRegistros} registro(s) encontrado(s)
          </span>
        </div>

        {loading && movimentos.length === 0 ? (
          <div className="p-8 text-center text-gray-500">Carregando movimentações...</div>
        ) : totalRegistros === 0 ? (
          <div className="p-8 text-center text-gray-500">
            Nenhuma movimentação de fundo de reserva encontrada
          </div>
        ) : (
          <>
            <div className={tableWrapperClass}>
              <table className={`${tableClass} min-w-[800px]`}>
                <thead className={tableHeadClass}>
                  <tr>
                    <th className={tableHeaderCellClass}>
                      Fundo
                    </th>
                    <th className={tableHeaderCellRightClass}>
                      Valor
                    </th>
                    <th className={tableHeaderCellCenterClass}>
                      Observação
                    </th>
                    <th className={tableHeaderCellRightClass}>
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className={tableBodyClass}>
                  {movimentosPaginados.map((item) => (
                    <tr key={item.id} className={tableRowClass}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {getNomeFundo(item)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 text-right">
                        {formatCurrencyMoeda(Number(item.valorMovimento ?? item.valor ?? 0))}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700 max-w-xs text-center">
                        <div className="truncate" title={item.observacao || item.descricao || ""}>
                          {item.observacao || item.descricao || "-"}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end gap-2">
                          <IconButton
                            onClick={() => handleEditar(item)}
                            disabled={processing}
                            icon={Edit2}
                            variant="info"
                            label="Editar"
                          />
                          <IconButton
                            onClick={() => handleDelete(item.id)}
                            disabled={processing}
                            icon={Trash2}
                            variant="danger"
                            label="Excluir"
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPaginas > 1 && (
              <div className="px-6 py-4 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-500">
                    Mostrando {indiceInicial + 1} a {Math.min(indiceFinal, totalRegistros)} de {totalRegistros} registros
                  </div>

                  <div className="flex items-center gap-2">
                    <IconButton
                      onClick={irParaPrimeiraPagina}
                      disabled={paginacao.paginaAtual === 1}
                      icon={ChevronsLeft}
                      label="Primeira página"
                    />

                    <IconButton
                      onClick={irParaPaginaAnterior}
                      disabled={paginacao.paginaAtual === 1}
                      icon={ChevronLeft}
                      label="Página anterior"
                    />

                    <div className="flex gap-1">
                      {gerarNumerosPaginas().map((pagina) => (
                        <Button
                          key={pagina}
                          onClick={() => irParaPagina(pagina)}
                          variant="ghost"
                          size="sm"
                          className={
                            pagina === paginacao.paginaAtual
                              ? buttonPageActiveClass
                              : buttonPageClass
                          }
                        >
                          {pagina}
                        </Button>
                      ))}
                    </div>

                    <IconButton
                      onClick={irParaProximaPagina}
                      disabled={paginacao.paginaAtual === totalPaginas}
                      icon={ChevronRight}
                      label="Próxima página"
                    />

                    <IconButton
                      onClick={irParaUltimaPagina}
                      disabled={paginacao.paginaAtual === totalPaginas}
                      icon={ChevronsRight}
                      label="Última página"
                    />
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </PageContainer>
  );
};

export default MovimentoFundoReserva;
